import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { nav, profile } from '../data/content';
import { FOCUS_RING } from './styles';

const SECTION_IDS = ['about', 'experience', 'projects', 'skills', 'awards', 'contact'];

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return progress;
}

function useActiveSection() {
  const [active, setActive] = useState(null);
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      // 맨 아래에서는 마지막 섹션이 짧아도 활성으로 본다
      if (window.innerHeight + window.scrollY >= doc.scrollHeight - 2) {
        setActive(SECTION_IDS[SECTION_IDS.length - 1]);
        return;
      }
      const marker = window.scrollY + window.innerHeight * 0.35;
      let current = null;
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= marker) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return active;
}

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const progress = useScrollProgress();
  const active = useActiveSection();

  const pill = (id) =>
    `whitespace-nowrap rounded-full px-3 py-1 transition-colors duration-300 ${FOCUS_RING} ${
      active === id ? 'bg-stone-200/80 font-medium text-stone-900' : 'text-stone-500 hover:text-stone-900'
    }`;

  const langButton = (value, label) => (
    <button
      type="button"
      onClick={() => setLang(value)}
      aria-pressed={lang === value}
      className={`h-8 px-3 transition-colors ${FOCUS_RING} ${
        lang === value ? 'bg-stone-900 text-stone-50' : 'text-stone-500 hover:text-stone-900'
      }`}
    >
      {label}
    </button>
  );

  const links = SECTION_IDS.map((id) => (
    <a key={id} href={`#${id}`} className={pill(id)} aria-current={active === id ? 'true' : undefined}>
      {t(nav[id])}
    </a>
  ));

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-stone-200 bg-stone-50/90 backdrop-blur">
      {/* 스크롤 진행 표시 */}
      <div
        className="absolute left-0 top-0 h-[2.5px] w-full origin-left bg-stone-900 transition-transform duration-100 ease-out"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-5xl px-5">
        <div className="flex h-14 items-center justify-between">
          <a href="#top" className={`rounded-sm text-lg font-bold tracking-tight ${FOCUS_RING}`}>
            {profile.nameEn}
            <span className="text-stone-400">.</span>
          </a>
          <div className="flex items-center gap-6">
            <nav className="hidden items-center gap-1 text-sm md:flex" aria-label="Primary">
              {links}
            </nav>
            <div
              className="flex items-center overflow-hidden rounded-full border border-stone-300 text-xs font-medium"
              role="group"
              aria-label="Language"
            >
              {langButton('ko', 'KO')}
              {langButton('en', 'EN')}
            </div>
          </div>
        </div>
        {/* 좁은 화면: 가로로 넘기는 메뉴 */}
        <nav
          className="-mx-5 flex gap-1 overflow-x-auto px-4 pb-2.5 text-[13px] [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden"
          aria-label="Primary"
        >
          {links}
        </nav>
      </div>
    </header>
  );
}
